import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { OperonLogo } from "@/components/OperonLogo";
import { useTheme } from "@/lib/theme";
import { api, extractErr } from "@/lib/api";
import { Sun, Moon, ArrowLeft, Mail } from "lucide-react";

export default function ForgotPassword() {
  const { theme, toggleTheme } = useTheme();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
      toast.success("Check your inbox for the reset link");
    } catch (e) {
      toast.error(extractErr(e));
    }
    setLoading(false);
  };

  return (
    <div className="min-h-dvh flex flex-col bg-background">
      <div className="flex items-center justify-between px-6 py-4 border-b border-border">
        <Link to="/" className="flex items-center"><OperonLogo theme={theme} size={32} /></Link>
        <Button variant="ghost" size="icon" onClick={toggleTheme}>
          {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
        </Button>
      </div>
      <div className="flex-1 flex items-center justify-center px-6">
        <form onSubmit={submit} className="w-full max-w-sm space-y-4" data-testid="forgot-password-form">
          <div>
            <h1 className="font-heading text-2xl font-semibold tracking-tight">Reset your password</h1>
            <p className="text-sm text-muted-foreground mt-1">We’ll email you a secure link to choose a new password.</p>
          </div>
          {sent ? (
            <div className="rounded-xl border border-border bg-card p-4 text-sm text-center space-y-2">
              <Mail className="w-8 h-8 text-primary mx-auto" />
              <div className="font-medium">Email sent</div>
              <p className="text-muted-foreground">If <span className="font-mono">{email}</span> has an account, a reset link is on its way. The link expires in 30 minutes.</p>
            </div>
          ) : (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-password-email-input" />
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="forgot-password-submit">
                {loading ? "Sending…" : "Send reset link"}
              </Button>
            </>
          )}
          <div className="text-center">
            <Link to="/login" className="text-sm text-primary hover:underline inline-flex items-center gap-1" data-testid="forgot-to-login"><ArrowLeft className="w-3.5 h-3.5" /> Back to login</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
